import { useMutation } from 'urql';
import { graphql } from '@/gql/graphql';
import useUploadImage from '@/hooks/useUploadImage';
import {
  getChatMediaCacheAsset,
  removeChatMediaCacheAsset,
} from '@/stores/useChatMediaCacheStore';
import {
  removeUploadMediaMeta,
  setUploadMediaMeta,
} from '@/stores/useChatStore';

const UpdateMessageMediaId = graphql(/* GraphQL */ `
  mutation UpdateMessageMediaId($id: uuid!, $mediaId: uuid!) {
    update_message_media_by_pk(
      pk_columns: { id: $id }
      _set: { media_id: $mediaId }
    ) {
      id
      media_id
    }
  }
`);
type UploadImage = ReturnType<typeof useUploadImage>;
type UpdateMessageMediaIdMutation = (variables: {
  id: string;
  mediaId: string;
}) => Promise<{
  error?: unknown;
}>;
function resetUploadMediaMeta(id: string) {
  setUploadMediaMeta(id, {
    isUploading: false,
  });
}
async function uploadCachedMessageImage({
  id,
  path,
  uploadImage,
  uploadUrl,
  updateMessageMediaId,
}: {
  id: string;
  path: string;
  uploadImage: UploadImage;
  uploadUrl: string;
  updateMessageMediaId: UpdateMessageMediaIdMutation;
}) {
  try {
    if (!uploadUrl) {
      resetUploadMediaMeta(id);
      return;
    }
    const asset = getChatMediaCacheAsset(id);
    if (!asset) {
      resetUploadMediaMeta(id);
      return;
    }
    const uploadPath = asset.uploadUri || asset.storageUri || asset.path;
    const mediaId = await uploadImage(
      path,
      uploadPath,
      uploadUrl,
      asset.mimeType,
    );
    if (!mediaId) {
      resetUploadMediaMeta(id);
      return;
    }
    const { error } = await updateMessageMediaId({
      id,
      mediaId,
    });
    if (error) {
      resetUploadMediaMeta(id);
      return;
    }
    await removeChatMediaCacheAsset(id);
    removeUploadMediaMeta(id);
  } catch {
    resetUploadMediaMeta(id);
  }
}
export default function useUploadMessageImage() {
  const uploadImage = useUploadImage();
  const [, updateMessageMediaId] = useMutation(UpdateMessageMediaId);
  const uploadMessageImage = async (
    id: string,
    path: string,
    uploadUrl: string,
  ) => {
    await uploadCachedMessageImage({
      id,
      path,
      uploadImage,
      uploadUrl,
      updateMessageMediaId,
    });
  };
  return uploadMessageImage;
}
